import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

const TYPE_MS = 40
const HOLD_MS = 2000
const FADE_MS = 300
const GAP_MS = 200

interface PlaceholderRotation {
    visibleText: string
    isVisible: boolean
    isCaretVisible: boolean
    isRunning: boolean
    stop: () => void
}

/**
 * Types each placeholder line out character by character, holds it, then fades the whole line and
 * starts the next — drawn by an aria-hidden overlay because `::placeholder` cannot be animated and
 * rewriting the attribute would spam screen reader announcements.
 */
export const usePlaceholderRotation = (
    lines: string[],
    containerRef: RefObject<HTMLElement | null>,
): PlaceholderRotation => {
    const [visibleText, setVisibleText] = useState('')
    const [isVisible, setIsVisible] = useState(true)
    const [isCaretVisible, setIsCaretVisible] = useState(true)
    const [isRunning, setIsRunning] = useState(true)

    // Typing a character re-renders the caller, which rebuilds `lines` with the same content but a
    // new identity. Keying the effect on that array restarted it on every keystroke of its own
    // animation, so key on the copy itself and read the array through a ref.
    const linesRef = useRef(lines)
    linesRef.current = lines
    const linesKey = lines.join('\u0000')

    // Ends the rotation for good: resuming on blur would leave a ghost behind the user's own text.
    const stop = useCallback(() => setIsRunning(false), [])

    useEffect(() => {
        const element = containerRef.current
        const lines = linesRef.current

        if (!isRunning || lines.length === 0 || !element) {
            return
        }

        if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            setVisibleText(lines[0])
            setIsCaretVisible(false)
            return
        }

        let timer: ReturnType<typeof setTimeout> | undefined
        let isPaused = false

        const wait = (ms: number, callback: () => void) => {
            timer = setTimeout(callback, ms)
        }

        const typeLine = (lineIndex: number) => {
            const line = lines[lineIndex]
            let characterCount = 0

            setIsVisible(true)
            setIsCaretVisible(true)

            const step = () => {
                characterCount += 1
                setVisibleText(line.slice(0, characterCount))

                if (characterCount < line.length) {
                    wait(TYPE_MS, step)
                    return
                }

                wait(HOLD_MS, () => {
                    setIsCaretVisible(false)
                    setIsVisible(false)

                    wait(FADE_MS + GAP_MS, () => {
                        setVisibleText('')
                        typeLine((lineIndex + 1) % lines.length)
                    })
                })
            }

            step()
        }

        // The sidebar copy of this field is CSS-hidden below 1024px, and the page is long — neither
        // should keep a timer running for an animation nobody can see.
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting === !isPaused) {
                return
            }

            isPaused = !entry.isIntersecting

            if (isPaused) {
                clearTimeout(timer)
            } else {
                typeLine(0)
            }
        })

        observer.observe(element)
        typeLine(0)

        return () => {
            observer.disconnect()
            clearTimeout(timer)
        }
    }, [containerRef, isRunning, linesKey])

    return { visibleText, isVisible, isCaretVisible, isRunning, stop }
}
